import type { AxiosResponse } from "axios";
import { ApiInstance } from "@/api";
import type {
  BaseProductDto,
  productWbNomenclatureDto,
  SizeInWbNomenclatureDto,
  WbCardDto,
  ProductsGroupedByWbProductNomenclatureID,
  OzonProduct,
  OzonClorsResponse,
} from "@/types/product/productTypes";
import type { ProductTableFilterType } from "@/stores/product/products/productsTable";

export default class Product {
  static async getProductsForTable(
    take: number = 2000,
    skip: number = 0,
    filter?: ProductTableFilterType,
  ): Promise<
    AxiosResponse<{
      item1: ProductsGroupedByWbProductNomenclatureID;
      item2: number;
    }>
  > {
    return ApiInstance.post<{
      item1: ProductsGroupedByWbProductNomenclatureID;
      item2: number;
    }>(`/product/productsForTable?take=${take}&skip=${skip}`, filter || {});
  }

  static async getBaseProduct(
    baseProductId: string,
  ): Promise<AxiosResponse<BaseProductDto>> {
    return ApiInstance.get<BaseProductDto>(
      `/product/BaseProduct/${baseProductId}`,
    );
  }

  static async getWbNomenclature(
    productNomenclatureId: string,
  ): Promise<AxiosResponse<productWbNomenclatureDto>> {
    return ApiInstance.get<productWbNomenclatureDto>(
      `/product/WbNomenclature/${productNomenclatureId}`,
    );
  }

  static async getProductOzone(
    ozonProductId: string,
  ): Promise<AxiosResponse<OzonProduct>> {
    return ApiInstance.get<OzonProduct>(
      `/product/OzonProduct/${ozonProductId}`,
    );
  }

  static async getOzonCardProducts(
    ozonProductId: string,
  ): Promise<AxiosResponse<OzonClorsResponse>> {
    return ApiInstance.get<OzonClorsResponse>(
      `/product/OzonCardProducts/${ozonProductId}`,
    );
  }

  static async getAllSizesInWbNomenclature(
    productNomenclatureId: string,
  ): Promise<AxiosResponse<SizeInWbNomenclatureDto[]>> {
    return ApiInstance.get<SizeInWbNomenclatureDto[]>(
      `/product/AllSizesInWbNomenclature/${productNomenclatureId}`,
    );
  }

  static async getWbCard(cardId: string): Promise<AxiosResponse<WbCardDto>> {
    return ApiInstance.get<WbCardDto>(`/product/WbCard/${cardId}`);
  }

  static async deleteProduct(ids: string[]): Promise<AxiosResponse> {
    return ApiInstance.delete("/product/Prodcut", {
      data: ids,
    });
  }

  static async bindWbNomenclatures(
    targetProductNomenclatureId: string,
    ids: string[],
  ): Promise<AxiosResponse> {
    return ApiInstance.put(
      `/product/ConnectVariant?nomenclatureId=${targetProductNomenclatureId}`,
      ids,
    );
  }

  static async unbindWbNomenclature(
    targetProductNomenclatureId: string,
  ): Promise<AxiosResponse> {
    return ApiInstance.put(
      `/product/DisconnectVariant?nomenclatureId=${targetProductNomenclatureId}`,
    );
  }

  static async unbindOzonNomenclature(
    ozonProductId: string,
  ): Promise<AxiosResponse> {
    return ApiInstance.put(
      `/product/DisconnectVariantOzon?ozonProductId=${ozonProductId}`,
    );
  }

  static async syncResidues(baseProductIdToSync: string) {
    return ApiInstance.put(
      `/product/SyncResidues?baseProductIdToSync=${baseProductIdToSync}`,
    );
  }

  static async changeGtinsForProducts(
    productGtinMap: Record<string, string | null> = {},
  ) {
    return ApiInstance.patch("/product/ChangeGtins", productGtinMap);
  }
}
